import {
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';

import type { OptionFacetCategoryType } from '~/types/types';
import featureFlags from '~/util/featureFlags';

import serverFeatureFlags from '../../../../../server/util/featureFlags';
import { MAX_RANGE_INDICATOR } from '../util/helpers';

type PriceFilterDefinition = {
  breakpoints: number[];
  isPerPerson: boolean;
};

const FALLBACK_PRICE_RANGE = [1000, 2000, 3000, 5000, 10000];

const searchResultsPagePriceRanges = getVendorTaxonomySwitchFunc<
  SearchableVendorTaxonomyKey,
  PriceFilterDefinition
>({
  [VENUES_TAXONOMY_KEY]: {
    breakpoints: [2000, 5000, 10000, 20000, 50000],
    isPerPerson: false,
  },
  [BANDS_DJS_TAXONOMY_KEY]: {
    breakpoints: [500, 1000, 2000, 6000],
    isPerPerson: false,
  },
  [CAKES_DESSERTS_TAXONOMY_KEY]: {
    breakpoints: [500, 2000],
    isPerPerson: false,
  },
  [FLORISTS_TAXONOMY_KEY]: {
    breakpoints: [2000, 3000, 5000, 10000],
    isPerPerson: false,
  },
  [HAIR_MAKEUP_TAXONOMY_KEY]: {
    breakpoints: [100, 200, 300, 500],
    isPerPerson: true,
  },
  [PHOTOGRAPHERS_TAXONOMY_KEY]: {
    breakpoints: [2000, 3000, 4000, 8000],
    isPerPerson: false,
  },
  [VIDEOGRAPHERS_TAXONOMY_KEY]: {
    breakpoints: [2000, 3000, 4000, 8000],
    isPerPerson: false,
  },
  [PLANNERS_TAXONOMY_KEY]: {
    breakpoints: [1000, 3000, 5000, 10000],
    isPerPerson: false,
  },
  [BAR_SERVICES_TAXONOMY_KEY]: {
    breakpoints: FALLBACK_PRICE_RANGE,
    isPerPerson: false,
  },
  [CATERING_TAXONOMY_KEY]: {
    breakpoints: FALLBACK_PRICE_RANGE,
    isPerPerson: false,
  },
});

const makePriceFilter = (): OptionFacetCategoryType => {
  return {
    displayName: 'Price',
    key: 'price',
    slug: 'price',
    facets: [],
  };
};

export const findPriceRangeForBudget = (
  taxonomyKey: SearchableVendorTaxonomyKey,
  serviceBudget: number
): number | typeof MAX_RANGE_INDICATOR => {
  const priceRangeDefinition = searchResultsPagePriceRanges(taxonomyKey);

  if (priceRangeDefinition.isPerPerson) {
    return MAX_RANGE_INDICATOR;
  }

  const foundBreakpoint = priceRangeDefinition.breakpoints.find((value) => value > serviceBudget);
  return foundBreakpoint || MAX_RANGE_INDICATOR;
};

export const createPricingFilter = makePriceFilter;

export const CAPACITY_BREAKPOINTS = [50, 100, 150, 200, 250, 300];
const MAX_CAPACITY = CAPACITY_BREAKPOINTS[CAPACITY_BREAKPOINTS.length - 1];

export const CAPACITY_FILTER: OptionFacetCategoryType = {
  key: 'capacity',
  displayName: 'Capacity',
  slug: 'capacity',
  facets: [
    ...CAPACITY_BREAKPOINTS.map((capacity, index) => {
      const nextLowerCapacity = CAPACITY_BREAKPOINTS[index - 1];

      const lowerText = nextLowerCapacity ? `${nextLowerCapacity}-` : 'Up to ';
      const lowerRange = String(nextLowerCapacity || 0);

      const slug = `capacity-${lowerRange}-${capacity}`;
      return {
        displayName: `${lowerText}${capacity}`,
        tagName: `${lowerText}${capacity} guests`,
        slug,
        key: slug,
        value: false,
      };
    }),
    {
      displayName: `${MAX_CAPACITY}+`,
      tagName: `${MAX_CAPACITY}+ guests`,
      slug: `capacity-${MAX_CAPACITY}-${MAX_RANGE_INDICATOR}`,
      key: `capacity-${MAX_CAPACITY}-${MAX_RANGE_INDICATOR}`,
      value: false,
    },
  ],
};
interface LocationDescriptionText {
  home: string;
  homeService: string;
  away: string;
}

const DEFAULT_LOCATION_DESCRIPTION: LocationDescriptionText = {
  home: 'Based in the wedding location itself.',
  homeService: 'Not local, but serves your location at no extra cost.',
  away: 'Happy to help, but may request extra to cover things like travel and accommodations.',
};

const getLocationDescriptions = getVendorTaxonomySwitchFunc<
  SearchableVendorTaxonomyKey,
  LocationDescriptionText
>({
  'wedding-venues': { away: '', home: '', homeService: '' },
  'wedding-photographers': {
    home: 'Based in the wedding location itself, these photographers might know all the best local spots.',
    homeService: 'Not local, but serves your location at no extra cost.',
    away: 'Happy to help, but may request extra to cover things like travel and accommodations.',
  },
  'wedding-videographers': {
    home: 'Based in the wedding location itself, these videographers might know all the best local spots.',
    homeService: 'Not local, but serves your location at no extra cost.',
    away: 'Happy to help, but may request extra to cover things like travel and accommodations.',
  },
  'wedding-hair-makeup': DEFAULT_LOCATION_DESCRIPTION,
  'wedding-bands-djs': DEFAULT_LOCATION_DESCRIPTION,
  'wedding-catering': DEFAULT_LOCATION_DESCRIPTION,
  'wedding-florists': DEFAULT_LOCATION_DESCRIPTION,
  'wedding-cakes-desserts': {
    home: 'Based in the wedding location itself. Less transport time from bakery to wedding.',
    homeService: 'Not local, but serves your location at no extra cost.',
    away: 'Happy to help, but may request extra to cover things like travel and accommodations.',
  },
  'wedding-planners': DEFAULT_LOCATION_DESCRIPTION,
  'wedding-bar-services': DEFAULT_LOCATION_DESCRIPTION,
});

export const createLocationFilter: (
  taxonomyKey: SearchableVendorTaxonomyKey
) => OptionFacetCategoryType = (taxonomyKey) => {
  const { away, home, homeService } = getLocationDescriptions(taxonomyKey);
  return {
    key: 'location',
    displayName: 'Location and fees',
    slug: 'metro-types',
    facets: [
      {
        displayName: 'Based in this city',
        tagName: 'Based in this city',
        key: 'HOME',
        slug: 'metro-types-home',
        value: false,
        description: home,
      },
      {
        displayName: 'Serves this city',
        tagName: 'Serves this city',
        key: 'HOME_SERVICE',
        slug: 'metro-types-home-service',
        value: false,
        description: homeService,
      },
      {
        displayName: 'Serves this city for additional fees',
        tagName: 'Serves this city for additional fees',
        key: 'AWAY',
        slug: 'metro-types-away',
        value: false,
        description: away,
      },
    ],
  };
};

export const createAwardGroupsFilter = (): OptionFacetCategoryType => {
  const latestAwardYear =
    typeof window !== 'undefined'
      ? featureFlags.get('bestOfZola2025')
        ? '2025'
        : '2024'
      : serverFeatureFlags.get('bestOfZola2025')
      ? '2025'
      : '2024';
  return {
    displayName: 'Award winners',
    facets: [
      {
        displayName: `Best of Zola ${latestAwardYear} Winners`,
        key: `BEST_OF_${latestAwardYear}`,
        slug: `award-groups-${latestAwardYear}`,
      },
      {
        displayName: 'All Best of Zola Winners',
        key: 'BEST_OF_ALL',
        slug: 'award-groups-all',
      },
    ],
    key: 'award-groups',
    slug: 'award-groups',
  };
};
